import React from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { getRoomRestrictionColor } from "../../utils/roomRestriction";

const splitIntoColumns = (items, columnCount = 3) => {
  const perColumn = Math.ceil(items.length / columnCount) || 0;
  const columns = [];
  for (let i = 0; i < columnCount; i += 1) {
    const start = i * perColumn;
    columns.push(items.slice(start, start + perColumn));
  }
  return columns;
};

export default function RouteTemplatesDrawer({
  open,
  onClose,
  selectedRouteTemplateId,
  onBackToTemplateList,
  routeTemplateDetail,
  routeTemplateDetailLoading,
  expandedRouteIds,
  pendingRouteAssignments,
  pendingManualAssignments,
  pendingEffectiveDates,
  todayDateKey,
  savingRouteId,
  savingManualRouteId,
  assignableTechnicianOptions,
  isAdmin,
  onToggleRouteExpand,
  onRouteAssigneeChange,
  onManualAssigneeChange,
  onEffectiveDateChange,
  onSaveRouteAssignment,
  onSaveManualAssignment,
  routeTemplates,
  onOpenTemplateDetail,
}) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, p: 2 }}>
        {selectedRouteTemplateId ? (
          <>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", mb: 1 }}
            >
              <IconButton size="small" onClick={onBackToTemplateList}>
                <ArrowBackOutlinedIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {routeTemplateDetail?.template_name || "Route Template"}
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1 }} />

            {routeTemplateDetailLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : routeTemplateDetail?.routes?.length > 0 ? (
              <List dense disablePadding>
                {routeTemplateDetail.routes.map((route) => {
                  const isExpanded = expandedRouteIds.has(route.pk_id);
                  const roomDetails = (route.rooms || []).map((room) => ({
                    roomName: room.room_name,
                    roomActive: room.active,
                    roomRestriction: room.room_restriction,
                  }));
                  const originalAssigneeValue = route.default_assigned_user_id
                    ? String(route.default_assigned_user_id)
                    : "";
                  const assigneeValue =
                    pendingRouteAssignments[route.pk_id] !== undefined
                      ? pendingRouteAssignments[route.pk_id]
                      : originalAssigneeValue;
                  const hasAssigneeChange =
                    pendingRouteAssignments[route.pk_id] !== undefined &&
                    pendingRouteAssignments[route.pk_id] !==
                      originalAssigneeValue;
                  const isSavingRoute = savingRouteId === route.pk_id;

                  const originalManualAssigneeValue =
                    route.default_assigned_manual_cleaning_user_id
                      ? String(route.default_assigned_manual_cleaning_user_id)
                      : "";
                  const manualAssigneeValue =
                    pendingManualAssignments[route.pk_id] !== undefined
                      ? pendingManualAssignments[route.pk_id]
                      : originalManualAssigneeValue;
                  const hasManualAssigneeChange =
                    pendingManualAssignments[route.pk_id] !== undefined &&
                    pendingManualAssignments[route.pk_id] !==
                      originalManualAssigneeValue;
                  const isSavingManualRoute =
                    savingManualRouteId === route.pk_id;

                  return (
                    <Box
                      key={route.pk_id}
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Box
                          onClick={() => onToggleRouteExpand(route.pk_id)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            cursor: "pointer",
                          }}
                        >
                          <Tooltip
                            title={
                              route.route_name
                                ? `${route.route_name} - ${route.route_code}`
                                : route.route_name
                            }
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                              noWrap
                            >
                              {route.route_name
                                ? route.route_name
                                : route.route_code}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary">
                            {`${roomDetails.length} room(s)`}
                          </Typography>
                        </Box>

                        <Tooltip title="Default device/robot job assignee">
                          <Select
                            size="small"
                            value={assigneeValue}
                            displayEmpty
                            onChange={(event) =>
                              onRouteAssigneeChange(
                                route.pk_id,
                                event.target.value,
                              )
                            }
                            renderValue={(value) => {
                              const option = assignableTechnicianOptions.find(
                                (opt) => String(opt.value) === String(value),
                              );
                              return option?.label || "Unassigned";
                            }}
                            sx={{
                              minWidth: 150,
                              fontSize: 13,
                              flexShrink: 0,
                            }}
                          >
                            <MenuItem value="">Unassigned</MenuItem>
                            {assignableTechnicianOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </Tooltip>

                        <Tooltip
                          title={
                            hasAssigneeChange
                              ? "Save assignee"
                              : "No changes to save"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onSaveRouteAssignment(route)}
                              disabled={!hasAssigneeChange || isSavingRoute}
                              sx={{
                                color: hasAssigneeChange
                                  ? "success.main"
                                  : "action.disabled",
                                flexShrink: 0,
                              }}
                            >
                              {isSavingRoute ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SaveOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <IconButton
                          size="small"
                          onClick={() => onToggleRouteExpand(route.pk_id)}
                          sx={{ flexShrink: 0 }}
                        >
                          <ExpandMoreOutlinedIcon
                            fontSize="small"
                            sx={{
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 150ms ease",
                            }}
                          />
                        </IconButton>
                      </Box>
                      {isAdmin && (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 2,
                              pb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              Manual cleaning assignee
                            </Typography>

                            <Select
                              size="small"
                              value={manualAssigneeValue}
                              displayEmpty
                              onChange={(event) =>
                                onManualAssigneeChange(
                                  route.pk_id,
                                  event.target.value,
                                )
                              }
                              renderValue={(value) => {
                                const option = assignableTechnicianOptions.find(
                                  (opt) => String(opt.value) === String(value),
                                );
                                return option?.label || "Unassigned";
                              }}
                              sx={{
                                minWidth: 150,
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              <MenuItem value="">Unassigned</MenuItem>
                              {assignableTechnicianOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>

                            <Tooltip
                              title={
                                hasManualAssigneeChange
                                  ? "Save manual cleaning assignee"
                                  : "No changes to save"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => onSaveManualAssignment(route)}
                                  disabled={
                                    !hasManualAssigneeChange ||
                                    isSavingManualRoute
                                  }
                                  sx={{
                                    color: hasManualAssigneeChange
                                      ? "success.main"
                                      : "action.disabled",
                                    flexShrink: 0,
                                  }}
                                >
                                  {isSavingManualRoute ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <SaveOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2,
                          pb: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          Effective from
                        </Typography>
                        <TextField
                          type="date"
                          size="small"
                          value={
                            pendingEffectiveDates[route.pk_id] || todayDateKey
                          }
                          onChange={(event) =>
                            onEffectiveDateChange(
                              route.pk_id,
                              event.target.value,
                            )
                          }
                          slotProps={{ InputLabel: { shrink: true } }}
                          sx={{
                            minWidth: 150,
                            flexShrink: 0,
                            "& .MuiInputBase-root": {
                              height: 40,
                            },
                            "& .MuiInputBase-input": {
                              boxSizing: "border-box",
                              padding: "8px 10px",
                              fontSize: "0.875rem",
                              fontFamily: "inherit",
                            },
                          }}
                        />
                      </Box>

                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 3, pr: 1, pt: 0.5, pb: 1 }}>
                          {roomDetails.length > 0 ? (
                            <Stack direction="row" spacing={2}>
                              {splitIntoColumns(roomDetails, 3).map(
                                (column, colIndex) => (
                                  <Box
                                    key={colIndex}
                                    sx={{ flex: 1, minWidth: 0 }}
                                  >
                                    {column.map(
                                      (
                                        {
                                          roomName,
                                          roomActive,
                                          roomRestriction,
                                        },
                                        index,
                                      ) => (
                                        <Tooltip
                                          key={`${route.pk_id}-${colIndex}-${index}`}
                                          title={`${roomName}${roomActive === 0 ? " - Disabled" : ""}`}
                                        >
                                          <Typography
                                            variant="caption"
                                            component="div"
                                            noWrap
                                            sx={{
                                              py: 0.15,
                                              textDecoration:
                                                roomActive === 0
                                                  ? "line-through"
                                                  : "none",
                                              color:
                                                getRoomRestrictionColor(
                                                  roomRestriction,
                                                ),
                                            }}
                                          >
                                            {roomName}
                                          </Typography>
                                        </Tooltip>
                                      ),
                                    )}
                                  </Box>
                                ),
                              )}
                            </Stack>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No rooms assigned
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No routes configured for this template.
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Route Templates
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {routeTemplates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No route templates found.
              </Typography>
            ) : (
              <List dense disablePadding>
                {routeTemplates.map((template) => (
                  <ListItem key={template.pk_id} disablePadding divider>
                    <ListItemButton
                      onClick={() => onOpenTemplateDetail(template.pk_id)}
                    >
                      <ListItemText
                        primary={template.template_name}
                        secondary={
                          `Rooms: ${template.route_rooms_count || 0}` +
                          (template.active === 0 ? " | Inactive" : "")
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
